import { useState, useEffect } from 'react';

/**
 * Hook genérico de debounce
 * @param value - O valor a ser debounced
 * @param delay - Tempo de delay em ms (padrão: 500ms)
 * @param validator - Função opcional para validar se o debounce deve ser aplicado
 * @returns O valor debounced
 */
export function useDebounce<T>(
  value: T,
  delay: number = 500,
  validator?: (value: T) => boolean
): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Se houver validador, só atualiza se passar na validação
    const shouldUpdate = validator ? validator(value) : true;

    if (!shouldUpdate) {
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay, validator]);

  return debouncedValue;
}

/**
 * Hook específico para debounce de datas (YYYY-MM-DD)
 * Só aplica o debounce quando a data está completa (10 caracteres) ou vazia
 */
export function useDateDebounce(value: string, delay: number = 500): string {
  return useDebounce(value, delay, (val) => val.length === 10 || val.length === 0);
}
